import { useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { TabGroup } from '@/components/ui/tab-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NodePropertyBadge, NodeAvatar, type MessageNodeConfig } from '@/components/nodes'
import { MessageThread } from '@/components/messages'
import { DetailPageLayout, LoadingState, PageContainer, PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { Status, MessageStatus, Priority, Horizon } from '@/types/enums'
import type { Task as TaskType, Message as MessageType } from '@/types/nodes'
import { toast } from 'sonner'
import { Edit, Trash2, MessageSquare, Loader2, Check, ChevronDown, ListTree } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// Generic types for the detail page
export interface NodeDetailConfig<T extends BaseNode> {
  nodeType: 'workspace' | 'project' | 'task'
  service: {
    getById: (id: string) => Promise<T | null>
    delete: (id: string) => Promise<void>
    updateStatus?: (id: string, status: Status) => Promise<void>
    getBlockedTasks?: (id: string) => Promise<TaskType[]>
  }
  tabs: TabConfig[]
  subTabs?: Record<string, TabConfig[]>
  getEditUrl: (id: string) => string
  getListUrl: string
  getMembersUrl?: (id: string) => string
  canEdit: (node: T, user: User | null) => boolean
  canDelete: (node: T, user: User | null) => boolean
  getMetadata: (node: T) => ReactNode
  getCustomActions?: (node: T, user: User | null) => ReactNode
  getTabContent: (node: T, tab: string, subTab?: string) => ReactNode
  getSidebarContent?: (node: T) => ReactNode
  getMainContent?: (node: T) => ReactNode
  getBadges: (node: T) => Record<string, number>
  getSubBadges?: (node: T, tab: string) => Record<string, number>
  loadingText: string
  notFoundText: string
  backToListText: string
  // MessageThread configuration
  messageConfig?: MessageNodeConfig
}

interface BaseNode {
  id: string
  name: string
  description?: string
  createdAt: Date
  updatedAt: Date
  creator: User
  users: User[]
  messages: MessageType[]
  documents: Document[]
  projects?: Project[]
  tasks?: TaskType[]
  workspace?: Workspace
  status?: Status
  priority?: Priority
  horizon?: Horizon
  dueDate?: Date
  assignee?: User
  subtasks?: TaskType[]
  dependencies?: TaskType[]
}

interface User {
  id: string
  name: string
  email: string
  role: string
}

interface Document {
  id: string
  name: string
  description: string
  type: string
  createdAt: Date
  creator: User
  projects: Project[]
  tasks: TaskType[]
}

interface Project {
  id: string
  name: string
  status: Status
  users: User[]
  tasks: TaskType[]
  messages: MessageType[]
  documents: Document[]
}

// Type guards for node-specific properties
interface TaskNode extends BaseNode {
  subtasks: TaskType[]
  dependencies: TaskType[]
  assignee?: User
  priority?: Priority
  horizon?: Horizon
  dueDate?: Date
  type: string
}

// Type guard to check if node has task properties
function isTaskNode(node: BaseNode): node is TaskNode {
  return 'subtasks' in node && 'dependencies' in node
}

interface Workspace {
  id: string
  name: string
  email: string
  users: User[]
  projects: Project[]
  tasks: TaskType[]
  messages: MessageType[]
  documents: Document[]
}

interface TabConfig {
  value: string
  label: string
  icon: LucideIcon
}

export function NodeDetailPage<T extends BaseNode>({ config }: { config: NodeDetailConfig<T> }) {
  const { [`${config.nodeType}Id`]: nodeId } = useParams<Record<string, string>>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [node, setNode] = useState<T | null>(null)
  const [blockedTasks, setBlockedTasks] = useState<TaskType[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [activeTab, setActiveTab] = useState(config.tabs[0]?.value || '')
  const [activeSubTabs, setActiveSubTabs] = useState<Record<string, string>>({})

  // Get top-level messages for threading (task-specific)
  const topLevelMessages = useMemo(() => {
    if (!node || config.nodeType !== 'task') return []

    return node.messages
      .filter((m: MessageType) => m.status !== MessageStatus.Draft && !m.parentId)
      .sort(
        (a: MessageType, b: MessageType) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
  }, [node, config.nodeType])

  // Get unique top-level messages, removing nested replies
  const uniqueTopLevelMessages = useMemo(() => {
    if (!node || config.nodeType !== 'task') return []

    const nestedMessageIds = new Set<string>()

    const collectNestedIds = (messages: MessageType[]) => {
      messages.forEach((msg: MessageType) => {
        if (msg.messages && msg.messages.length > 0) {
          msg.messages.forEach((nested: MessageType) => {
            nestedMessageIds.add(nested.id)
            collectNestedIds(msg.messages)
          })
        }
      })
    }

    topLevelMessages.forEach((msg: MessageType) => {
      collectNestedIds([msg])
    })

    return topLevelMessages.filter((message: MessageType) => !nestedMessageIds.has(message.id))
  }, [topLevelMessages, node, config.nodeType])

  const loadNode = useCallback(async () => {
    if (!nodeId) return

    try {
      setLoading(true)
      const nodeData = await config.service.getById(nodeId)
      if (nodeData) {
        setNode(nodeData)

        // Load additional data for tasks
        if (config.nodeType === 'task' && config.service.getBlockedTasks) {
          const blocked = await config.service.getBlockedTasks(nodeId)
          setBlockedTasks(blocked)
        }
      } else {
        toast.error(`${config.nodeType} not found`)
        navigate(config.getListUrl)
      }
    } catch (error) {
      console.error(`Failed to load ${config.nodeType}:`, error)
      toast.error(`Failed to load ${config.nodeType}`)
      navigate(config.getListUrl)
    } finally {
      setLoading(false)
    }
  }, [nodeId, navigate, config])

  const handleReplyCreated = useCallback(async () => {
    await loadNode()
  }, [loadNode])

  useEffect(() => {
    loadNode()
  }, [loadNode])

  const handleDelete = async () => {
    if (!node || !nodeId) return

    if (!config.canDelete(node, user)) {
      toast.error(`You don't have permission to delete this ${config.nodeType}`)
      return
    }

    if (!confirm(`Are you sure you want to delete "${node.name}"? This action cannot be undone.`)) {
      return
    }

    setDeleting(true)
    try {
      await config.service.delete(nodeId)
      toast.success(`${config.nodeType} deleted successfully`)
      navigate(config.getListUrl)
    } catch (error) {
      console.error(`Failed to delete ${config.nodeType}:`, error)
      toast.error(`Failed to delete ${config.nodeType}`)
      setDeleting(false)
    }
  }

  const handleStatusChange = useCallback(
    async (newStatus: Status) => {
      if (!nodeId || !node || !config.service.updateStatus) return

      try {
        await config.service.updateStatus(nodeId, newStatus)
        setNode({ ...node, status: newStatus } as T)
        toast.success(`${config.nodeType} status updated to ${newStatus}`)
      } catch (error) {
        console.error(`Failed to update ${config.nodeType}:`, error)
        toast.error(`Failed to update ${config.nodeType}`)
      }
    },
    [nodeId, node, config]
  )

  const getButtonStatusClasses = (status: Status | undefined) => {
    if (!status) return ''

    switch (status) {
      case Status.Draft:
        return 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800 dark:hover:bg-yellow-900'
      case Status.ToDo:
        return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900'
      case Status.InProgress:
        return 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-950 dark:text-green-300 dark:border-green-800 dark:hover:bg-green-900'
      case Status.Done:
        return 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-950 dark:text-gray-400 dark:border-gray-800 dark:hover:bg-gray-900'
      default:
        return ''
    }
  }

  const isTaskBlocked = useCallback((blockedTask: TaskType) => {
    return blockedTask.dependencies.some((dependency) => dependency.status !== Status.Done)
  }, [])

  const isTaskUnblocked = useCallback((blockedTask: TaskType) => {
    return blockedTask.dependencies.every((dependency) => dependency.status === Status.Done)
  }, [])

  if (loading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState variant="spinner" text={config.loadingText} />
      </PageContainer>
    )
  }

  if (!node) {
    return (
      <PageContainer maxWidth="full">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">{config.notFoundText}</p>
            <Button className="mt-4" onClick={() => navigate(config.getListUrl)}>
              {config.backToListText}
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  const canEdit = config.canEdit(node, user)
  const customActions = config.getCustomActions?.(node, user)

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title={node.name}
        description={node.description}
        metadata={config.getMetadata(node)}
        customActions={
          <div className="flex gap-2">
            {canEdit && (
              <Button variant="outline" onClick={() => navigate(config.getEditUrl(node.id))}>
                <Edit className="mr-2 h-4 w-4" />
                Edit {config.nodeType}
              </Button>
            )}
            {canEdit && config.service.updateStatus && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className={getButtonStatusClasses(node.status)} variant="outline">
                    <Check className="mr-2 h-4 w-4" />
                    {node.status === Status.Done ? 'Completed' : `${node.status || 'Unknown'}`}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleStatusChange(Status.Draft)}>
                    <span className={node.status === Status.Draft ? 'font-semibold' : ''}>
                      Draft
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange(Status.ToDo)}>
                    <span className={node.status === Status.ToDo ? 'font-semibold' : ''}>
                      To Do
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange(Status.InProgress)}>
                    <span className={node.status === Status.InProgress ? 'font-semibold' : ''}>
                      In Progress
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange(Status.Done)}>
                    <span className={node.status === Status.Done ? 'font-semibold' : ''}>Done</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canEdit && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="text-destructive hover:text-destructive"
              >
                {deleting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Delete
              </Button>
            )}
            {customActions}
          </div>
        }
      />

      {config.getMainContent ? (
        config.getMainContent(node)
      ) : config.getSidebarContent ? (
        <DetailPageLayout sidebar={config.getSidebarContent(node)}>
          <div className="mt-6 flex flex-col gap-6">
            {/* Description */}
            {node.description && (
              <Card>
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{node.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Messages */}
            {config.nodeType === 'task' && uniqueTopLevelMessages.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Messages ({uniqueTopLevelMessages.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col space-y-3">
                    {uniqueTopLevelMessages.map((message: MessageType) => (
                      <MessageThread
                        key={message.id}
                        message={message}
                        showTitle={false}
                        showDescription={true}
                        messageConfig={config.messageConfig}
                        onReplyCreated={handleReplyCreated}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Task-specific content */}
            {config.nodeType === 'task' && isTaskNode(node) && (
              <>
                {/* Subtasks */}
                {node.subtasks?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ListTree className="h-5 w-5" />
                        Subtasks (
                        {node.subtasks.filter((st: TaskType) => st.status === Status.Done).length}/
                        {node.subtasks.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {node.subtasks.map((subtask: TaskType) => (
                          <Link key={subtask.id} to={`/tasks/${subtask.id}`}>
                            <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors">
                              <NodePropertyBadge
                                type="status"
                                value={subtask.status}
                                iconSize="sm"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{subtask.name}</p>
                                {subtask.description && (
                                  <p className="text-sm text-muted-foreground truncate">
                                    {subtask.description}
                                  </p>
                                )}
                              </div>
                              {subtask.assignee && <NodeAvatar node={subtask.assignee} size="sm" />}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Dependencies */}
                {node.dependencies?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">Dependencies</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        This task depends on {node.dependencies.length}{' '}
                        {node.dependencies.length === 1 ? 'task' : 'tasks'} •{' '}
                        {node.dependencies.filter((d: TaskType) => d.status === Status.Done).length}{' '}
                        completed
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-3">
                        {node.dependencies.map((dependency: TaskType) => {
                          const isDone = dependency.status === Status.Done
                          return (
                            <Link key={dependency.id} to={`/tasks/${dependency.id}`}>
                              <div
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                  isDone
                                    ? 'bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-900 hover:bg-green-100/50 dark:hover:bg-green-950/30'
                                    : 'border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20 hover:bg-orange-100/50 dark:hover:bg-orange-950/30'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{dependency.name}</p>
                                  {dependency.description && (
                                    <p className="text-sm text-muted-foreground truncate">
                                      {dependency.description}
                                    </p>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {isDone ? '✓ Completed' : '⏱ Pending'}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          )
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Blocked Tasks */}
                {blockedTasks.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        Blocks {blockedTasks.length} {blockedTasks.length === 1 ? 'Task' : 'Tasks'}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {blockedTasks.filter(isTaskUnblocked).length > 0
                          ? `${blockedTasks.filter(isTaskUnblocked).length} of ${blockedTasks.length} tasks can now proceed`
                          : 'These tasks are waiting for their dependencies to complete'}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col space-y-3">
                        {blockedTasks.map((blockedTask) => {
                          const unblocked = isTaskUnblocked(blockedTask)
                          const blocked = isTaskBlocked(blockedTask)
                          return (
                            <Link key={blockedTask.id} to={`/tasks/${blockedTask.id}`}>
                              <div
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                  unblocked
                                    ? 'bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-900 hover:bg-green-100/50 dark:hover:bg-green-950/30'
                                    : blocked
                                      ? 'border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20 hover:bg-orange-100/50 dark:hover:bg-orange-950/30'
                                      : 'border-gray-200 dark:border-gray-800 hover:bg-accent'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{blockedTask.name}</p>
                                  {blockedTask.description && (
                                    <p className="text-sm text-muted-foreground truncate">
                                      {blockedTask.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 mt-1">
                                    <p className="text-xs text-muted-foreground">
                                      {unblocked
                                        ? '✓ Ready to proceed'
                                        : blocked
                                          ? '⏱ Waiting for dependencies'
                                          : '🔒 Blocked'}
                                    </p>
                                    {blocked && (
                                      <span className="text-xs text-muted-foreground">
                                        (
                                        {
                                          blockedTask.dependencies.filter(
                                            (d) => d.status !== Status.Done
                                          ).length
                                        }{' '}
                                        pending)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Link>
                          )
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </DetailPageLayout>
      ) : (
        <div className="space-y-6 mt-6">
          {/* Tabbed Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabGroup
              tabs={config.tabs}
              badges={config.getBadges(node)}
              value={activeTab}
              onValueChange={setActiveTab}
            />

            {config.tabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                {config.subTabs?.[tab.value] ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <tab.icon className="h-5 w-5" />
                        {tab.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Tabs
                        value={activeSubTabs[tab.value] || config.subTabs![tab.value][0]?.value}
                        onValueChange={(value) =>
                          setActiveSubTabs((prev) => ({ ...prev, [tab.value]: value }))
                        }
                        className="w-full"
                      >
                        <TabGroup
                          tabs={config.subTabs![tab.value]}
                          badges={config.getSubBadges?.(node, tab.value) || {}}
                          value={activeSubTabs[tab.value] || config.subTabs![tab.value][0]?.value}
                          onValueChange={(value) =>
                            setActiveSubTabs((prev) => ({ ...prev, [tab.value]: value }))
                          }
                        />

                        {config.subTabs![tab.value].map((subTab) => (
                          <TabsContent key={subTab.value} value={subTab.value}>
                            {config.getTabContent(node, tab.value, subTab.value)}
                          </TabsContent>
                        ))}
                      </Tabs>
                    </CardContent>
                  </Card>
                ) : (
                  config.getTabContent(node, tab.value)
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    </PageContainer>
  )
}
